import * as R from 'ramda';

import { GeoJSONGeometry } from '@atom/types/geojson';

import { getGoogleShapeCenter, latLngToUrlValue } from '../mapUtilities';

const locationKey = (location: unknown): string =>
  JSON.stringify(
    (location as { coordinates?: unknown } | null)?.coordinates ?? null,
  );

export const partitionByLocation = <T extends { location?: unknown }>(
  items: T[],
): { singles: T[]; groups: T[][] } => {
  const buckets = R.values(
    R.groupBy(item => locationKey(item.location), items),
  );

  const [groupBuckets, singleBuckets] = R.partition(
    bucket => bucket.length > 1,
    buckets,
  );

  return {
    singles: singleBuckets.map(bucket => bucket[0]),
    groups: groupBuckets,
  };
};

export const groupIdOf = (items: { id: string }[]): string =>
  items.map(item => item.id).join(',');

// A compact, URL-safe identifier for a stack of items that share a map
// location. Because every member of a group has identical coordinates, the
// group's center uniquely identifies the stack.
export const stackKeyOf = (items: { location: GeoJSONGeometry }[]): string =>
  latLngToUrlValue(getGoogleShapeCenter(items[0].location));

// Re-derives the members of a stack from in-memory map data by matching each
// item's location key against the one encoded in the URL.
export const itemsInStack = <T extends { location: GeoJSONGeometry }>(
  items: T[],
  stackKey: string,
): T[] =>
  items.filter(
    item => latLngToUrlValue(getGoogleShapeCenter(item.location)) === stackKey,
  );
